import { serviceLinks, industryLinks, knowledgeDropdownLinks } from "@/components/navbar/navData";
import { FILTER_DISPLAY_NAMES } from "@/components/blog/BlogFilterSidebar";
import { KNOWLEDGE_CENTER_TYPES, EXCLUDED_FILTER_CATEGORIES, getContentTypeLabel, type ContentTypeKey } from "@/lib/resourceTypes";
import { Monitor, ChevronDown } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import type { LucideIcon } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { navT } from "@/i18n/nav";
import { uiT } from "@/i18n/ui";

const formatIcons: Record<string, LucideIcon> = Object.fromEntries(
  knowledgeDropdownLinks
    .filter((l) => l.contentTypeKey && l.icon)
    .map((l) => [l.contentTypeKey!, l.icon!])
);

const industryIconMap = Object.fromEntries(industryLinks.map((l) => [l.label, l.icon])) as Record<string, LucideIcon>;
industryIconMap["MDSW"] = Monitor;

const industries = [...industryLinks.map((l) => l.label), "MDSW"];
const services = serviceLinks.map((l) => l.label);
const serviceColorMap = Object.fromEntries(serviceLinks.map((l) => [l.label, l.color]));

interface ResourceFilterSidebarProps {
  selectedFilters: string[];
  onFilterChange: (filters: string[]) => void;
  availableFilters: string[];
  selectedFormats: ContentTypeKey[];
  onFormatChange: (formats: ContentTypeKey[]) => void;
}

const ResourceFilterSidebar = ({
  selectedFilters,
  onFilterChange,
  availableFilters,
  selectedFormats,
  onFormatChange,
}: ResourceFilterSidebarProps) => {
  const lang = useLanguage();
  const t = uiT(lang);

  // Alias map: service/industry labels → alternative tag names used in content
  const TAG_ALIASES: Record<string, string[]> = {
    "Pharmacovigilance": ["Safety Reporting", "Vigilance"],
    "Software Solutions & Services": ["MDSW", "SaMD", "Software"],
    "Clinical Solutions": ["Clinical", "CDx", "Companion Diagnostics (CDx)"],
  };

  const matchesCategory = (filter: string, category: string) => {
    const fl = filter.toLowerCase();
    const cl = category.toLowerCase();
    if (fl === cl) return true;
    const aliases = TAG_ALIASES[category] ?? [];
    if (aliases.some((a) => fl === a.toLowerCase())) return true;
    // Reverse: check if filter is an alias target
    for (const [key, vals] of Object.entries(TAG_ALIASES)) {
      if (key.toLowerCase() === cl && vals.some((v) => v.toLowerCase() === fl)) return true;
    }
    return false;
  };

  const availableIndustries = industries.filter((i) =>
    availableFilters.some((f) => matchesCategory(f, i))
  );
  const availableServices = services.filter((s) =>
    availableFilters.some((f) => matchesCategory(f, s))
  );
  const getFilterValue = (categoryLabel: string) =>
    availableFilters.find((f) => matchesCategory(f, categoryLabel)) ?? categoryLabel;

  const excludedTopics = new Set(EXCLUDED_FILTER_CATEGORIES);
  const isExcluded = (f: string) => excludedTopics.has(f) || f.toLowerCase().includes("life sciences");
  const specificTopicsRaw = availableFilters.filter(
    (f) => !isExcluded(f) && !industries.some((i) => matchesCategory(f, i)) && !services.some((s) => matchesCategory(f, s))
  );
  // Deduplicate: case-insensitive + collapse display-name aliases
  const specificTopics = (() => {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const f of specificTopicsRaw) {
      const display = (FILTER_DISPLAY_NAMES[f] ?? f).toLowerCase();
      if (!seen.has(display) && !seen.has(f.toLowerCase())) {
        seen.add(display);
        seen.add(f.toLowerCase());
        result.push(f);
      }
    }
    return result;
  })();

  const toggleFilter = (filterVal: string) => {
    if (selectedFilters.includes(filterVal)) {
      onFilterChange(selectedFilters.filter((f) => f !== filterVal));
    } else {
      onFilterChange([...selectedFilters, filterVal]);
    }
  };

  const toggleFormat = (key: ContentTypeKey) => {
    if (selectedFormats.includes(key)) {
      onFormatChange(selectedFormats.filter((f) => f !== key));
    } else {
      onFormatChange([...selectedFormats, key]);
    }
  };

  const FilterItem = ({ label, value }: { label: string; value?: string }) => {
    const filterVal = value ?? label;
    const isActive = selectedFilters.includes(filterVal);
    return (
      <button
        onClick={() => toggleFilter(filterVal)}
        className={`block w-full text-left px-3 py-1.5 text-sm rounded-lg transition-colors ${
          isActive
            ? "text-primary font-semibold bg-primary/5"
            : "text-foreground/70 hover:text-primary hover:bg-primary/3"
        }`}
      >
        {label}
      </button>
    );
  };

  return (
    <aside className="space-y-6">
      {/* Format filter */}
      <div>
        <h3 className="text-sm font-bold text-foreground mb-2 px-3">{t("format")}</h3>
        <div className="space-y-0.5">
          {KNOWLEDGE_CENTER_TYPES.map((ct) => {
            const isActive = selectedFormats.includes(ct.key);
            return (
              <button
                key={ct.key}
                onClick={() => toggleFormat(ct.key)}
                className={`block w-full text-left px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  isActive
                    ? "text-primary font-semibold bg-primary/5"
                    : "text-foreground/70 hover:text-primary hover:bg-primary/3"
                }`}
              >
                {(() => { const Icon = formatIcons[ct.key]; return Icon ? <Icon size={14} className="inline-block mr-2 -mt-0.5" /> : null; })()}
                {getContentTypeLabel(ct.key, lang)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Industries */}
      {availableIndustries.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-foreground mb-2 px-3">{t("allIndustries")}</h3>
          <div className="space-y-0.5">
            {availableIndustries.map((item) => {
              const filterVal = getFilterValue(item);
              const isActive = selectedFilters.includes(filterVal);
              const Icon = industryIconMap[item];
              return (
                <button
                  key={item}
                  onClick={() => toggleFilter(filterVal)}
                  className={`block w-full text-left px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    isActive
                      ? "text-primary font-semibold bg-primary/5"
                      : "text-foreground/70 hover:text-primary hover:bg-primary/3"
                  }`}
                >
                  {Icon && <Icon size={14} className="inline-block mr-2 -mt-0.5" />}
                  {navT(item, lang)}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Services */}
      {availableServices.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-foreground mb-2 px-3">{t("allServices")}</h3>
          <div className="space-y-0.5">
            {availableServices.map((item) => {
              const filterVal = getFilterValue(item);
              const isActive = selectedFilters.includes(filterVal);
              const color = serviceColorMap[item];
              return (
                <button
                  key={item}
                  onClick={() => toggleFilter(filterVal)}
                  className={`block w-full text-left px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    isActive
                      ? "text-primary font-semibold bg-primary/5"
                      : "text-foreground/70 hover:text-primary hover:bg-primary/3"
                  }`}
                >
                  {color && <span className="inline-block w-2 h-2 rounded-full mr-2 -mt-0.5 align-middle" style={{ backgroundColor: color }} />}
                  {navT(item, lang)}
                </button>
              );
            })}
          </div>
        </div>
      )}

    </aside>
  );
};

export default ResourceFilterSidebar;
